import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import config from '../config';
import openPdf from '../utils/openPdf';
import { CoatingSwatch } from '../components/CoatingColor';
import { detailToString } from '../utils/apiError';
import { emailSenderHeaders } from '../utils/emailSender';
import { findPallets, showPallets } from '../features/pallets/opoPalletFlows';
import { requestAdminPin } from '../adminSession';
import pinPrompt from '../utils/pinPrompt';
import { earliest, palletTickState } from '../features/outsourcing/palletBuckets';
import {
  ageBadge, allocatedAwayText, atVendorFacts, bucketsByGroup as bucketsByGroupOf, fmtDate,
  fmtMoney, fmtQty, groupPalletIds, groupSelection, hasMixedProcesses, isDeadLine, isManifest,
  lineIdentity as lineIdentityOf, isOtherOrderPallet, jobLineNotes,
  lineBack, lineRemaining, linkedPalletNames, loadCoatingBoard, loadServiceVendors, manifestBuckets,
  matchingDrafts as matchingDraftsFor, ownShareDone, palletPackedTotal, parseServerError, pruneSelection,
  receiveOfficePrefill,
  receiveRowsFor, selectionVendorId as selectionVendorIdOf, stagingGroups as stagingGroupsOf, tickedPallets,
  toggleGroupIds, togglePalletIds, toggleTaskIds, vendorOverride,
} from '../features/outsourcing/coatingData';
import {
  addToDraft, beaconsBackOn, createFlowGuard, createManifest, createPalletManifest, desktopCoatingUi,
  printPalletLabels, receiveManifest, removeLine, reprintPaper, sendManifest as sendManifestAction,
} from '../features/outsourcing/coatingActions';
import { useShippingSigningFlags } from '../features/shippingHub/signingFlags';
import { vendorSignChip, vendorSigningOn } from '../features/signing/vendorSigning';

const API_BASE = config.API_BASE_URL;

/**
 * Coating Management — the office's send/receive desk for outside coating.
 *
 * Unblocked OUTSOURCED tasks pool in a STAGING queue (GET /coating/board) — the
 * endpoint is general despite its path; it selects via _sql_task_is_outsourced;
 * the office batches them (one sales order, one vendor) onto multi-line
 * MANIFESTS (Outside Processing Orders) via POST /coating/manifests, sends
 * the batch (prints the paper), and receives it back line-by-line — each
 * fully-covered line completes its coating task and unblocks the next
 * station. Tabs mirror the physical flow:
 *   Staging → Manifests (Drafts) → At Vendor → History
 *
 * mode="operator" (the Shipping hub's Outsourcing tab, Phase 1, 2026-10-27; `ui` = workingAsUi(desktopUi)):
 *   tabs read To send / Loading / At vendor (Receive back) / History, no Short tab; create, add, remove, send
 *   and receive carry the Working-as session's token as `pin` (who did it); the send's pallet checklist is
 *   strict (a failed read stops the send). Office-only until "Office tools" (Admin PIN, this screen only):
 *   vendor email, invoice entry, Delete draft, the receive window's invoice # / cost / freight (left out of the
 *   request while hidden), and "send without the checklist". The office page (no mode) is unchanged.
 */
// The reads, the lists and the rules are features/outsourcing/coatingData.js; every write is
// features/outsourcing/coatingActions.js (2026-10-29: the phone Shipping screen's Outsourcing tab calls the same).

export default function CoatingManagementPage({ mode = 'office', ui = null } = {}) {
  const operator = mode === 'operator';
  const [officeUnlocked, setOfficeUnlocked] = useState(false);
  const office = !operator || officeUnlocked;
  const [currentTab, setCurrentTab] = useState('staging');
  const [board, setBoard] = useState({ staging: [], in_production: [], short_supply: [], counts: {} });
  const [opos, setOpos] = useState([]);
  const [serviceVendors, setServiceVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  // Vendor receipt signing (jobq-api 2026-10-29, GET /features shipping_signing.vendor_receipt): off = nothing new shows
  const signing = useShippingSigningFlags();
  const vendorOn = vendorSigningOn(signing);

  // Staging selection (task_ids)
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [addToDraftId, setAddToDraftId] = useState('');

  // Create-manifest modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createVendorId, setCreateVendorId] = useState('');
  const [createDueBack, setCreateDueBack] = useState('');
  const [createNotes, setCreateNotes] = useState('');

  // Receive modal
  const [receiveOpo, setReceiveOpo] = useState(null);
  // Late invoice entry: once a manifest goes Returned it leaves the At Vendor
  // tab and the backend 409s a second receive, so actual_cost was unreachable
  // from the UI forever. This reopens just that field.
  const [invoiceOpo, setInvoiceOpo] = useState(null);
  const [invoiceCost, setInvoiceCost] = useState('');
  const [invoiceFreight, setInvoiceFreight] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [receiveRows, setReceiveRows] = useState({});
  const [receiveInvoice, setReceiveInvoice] = useState('');
  const [receiveCost, setReceiveCost] = useState('');
  const [receiveFreight, setReceiveFreight] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { board: boardData, opos: opoData } = await loadCoatingBoard(API_BASE);
      setBoard(boardData);
      setOpos(opoData);
      // Drop selections that left the staging pool (batched elsewhere, voided...)
      setSelectedTaskIds((prev) => pruneSelection(prev, boardData.staging));
    } catch (err) {
      console.error('Failed to load coating board:', err);
      setLoadError(err.message || 'Failed to load the coating board.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // How the shared actions (features/outsourcing/coatingActions) talk to the person here: the native dialogs and
  // the print dialog; operator mode: who did it is the Working-as session's token (a sign-in when there is none),
  // the office page sends no pin (as before).
  const cui = useMemo(() => desktopCoatingUi({ operator, ui }), [operator, ui]);

  async function unlockOffice() {
    const admin = await requestAdminPin();
    if (admin) setOfficeUnlocked(true);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await loadServiceVendors(API_BASE);
      if (!cancelled) setServiceVendors(data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Derived buckets -----------------------------------------------------

  const { drafts: draftManifests, atVendor: atVendorOpos, history: historyOpos } = useMemo(
    () => manifestBuckets(opos),
    [opos]
  );

  // Staging rows grouped by sales order (features/outsourcing/coatingData stagingGroups: a job split by
  // allocation is also listed under each order its pieces are packed for).
  const stagingGroups = useMemo(() => stagingGroupsOf(board.staging), [board.staging]);

  // Pallet first (Mike, 2026-10-28): each order's pallets, the parts on each with one total, the jobs under it
  // (features/outsourcing/palletBuckets.js). Tasks on no pallet yet are listed one row each, as before.
  const bucketsByGroup = useMemo(() => bucketsByGroupOf(stagingGroups), [stagingGroups]);

  const stagingByTaskId = useMemo(() => {
    const m = new Map();
    (board.staging || []).forEach((t) => m.set(t.task_id, t));
    return m;
  }, [board.staging]);

  const selectedTasks = useMemo(
    () => [...selectedTaskIds].map((id) => stagingByTaskId.get(id)).filter(Boolean),
    [selectedTaskIds, stagingByTaskId]
  );

  // The selection's UNANIMOUS default vendor (prefills the create modal).
  const selectionVendorId = useMemo(() => selectionVendorIdOf(selectedTasks), [selectedTasks]);

  // Draft manifests the current selection could join (same SO).
  const matchingDrafts = useMemo(
    () => matchingDraftsFor(draftManifests, selectedTasks),
    [draftManifests, selectedTasks]
  );

  useEffect(() => {
    // Keep the Add-to-Draft pick valid as the matching set changes.
    if (matchingDrafts.length === 0) {
      if (addToDraftId) setAddToDraftId('');
      return;
    }
    if (!matchingDrafts.some((o) => String(o.opo_id) === addToDraftId)) {
      setAddToDraftId(String(matchingDrafts[0].opo_id));
    }
  }, [matchingDrafts, addToDraftId]);

  // ---- Staging selection guards --------------------------------------------

  function applySelection(r) {
    if (r.refuse) window.alert(r.refuse);
    else setSelectedTaskIds(r.ids);
  }

  function toggleTask(task) {
    applySelection(toggleTaskIds(selectedTaskIds, selectedTasks, task));
  }

  function toggleGroup(group, checked) {
    applySelection(toggleGroupIds(selectedTaskIds, selectedTasks, group, checked));
  }

  /** Tick / untick a pallet: its own tasks, and every pallet sharing one of them (one paper carries a task's
   *  pieces on all its pallets). */
  function togglePallet(group, pallets, key, checked) {
    applySelection(togglePalletIds(selectedTaskIds, selectedTasks, group, pallets, key, checked));
  }

  // ---- Actions (features/outsourcing/coatingActions) -------------------------

  function openCreateModal() {
    setCreateVendorId(selectionVendorId != null ? String(selectionVendorId) : '');
    setCreateDueBack('');
    setCreateNotes('');
    setShowCreateModal(true);
  }

  async function handleCreateManifest() {
    if (!selectedTaskIds.size) return;
    await createManifest({
      apiBase: API_BASE, cui, taskIds: [...selectedTaskIds], vendorId: createVendorId,
      dueBack: createDueBack, notes: createNotes, setBusy,
      onDone: async () => {
        setShowCreateModal(false);
        setSelectedTaskIds(new Set());
        await loadAll();
        setCurrentTab('drafts');
      },
    });
  }

  async function createPalletPaper(palletShortId) {
    await createPalletManifest({
      apiBase: API_BASE, cui, palletShortId, setBusy,
      onDone: async () => {
        setSelectedTaskIds(new Set());
        await loadAll();
        setCurrentTab('drafts');
      },
    });
  }

  async function handleAddToDraft() {
    const target = matchingDrafts.find((o) => String(o.opo_id) === addToDraftId);
    if (!target || !selectedTaskIds.size) return;
    await addToDraft({
      apiBase: API_BASE, cui, target, taskIds: [...selectedTaskIds], setBusy,
      onDone: async () => {
        setSelectedTaskIds(new Set());
        await loadAll();
        setCurrentTab('drafts');
      },
    });
  }

  async function handleRemoveLine(opo, line) {
    await removeLine({ apiBase: API_BASE, cui, opo, line, setBusy, onDone: loadAll });
  }

  // One pallet flow (send / find / beacons back on) at a time: the checklist reads the API before anything
  // shows, and a second click in that moment opened a second send (review 2026-10-26).
  const runPalletFlow = useRef(createFlowGuard()).current;

  async function handleBeaconsBackOn(opo) {
    await runPalletFlow(() => beaconsBackOn({ cui, opo }));
  }

  function handleSendManifest(opo) {
    return runPalletFlow(() => sendManifestAction({
      apiBase: API_BASE, cui, opo, operator,
      allowUnchecked: operator && officeUnlocked
        ? (message) => window.confirm(`${message}\n\nOffice tools: send it WITHOUT the pallet checklist?`)
        : null,
      offerEmail: office,
      reload: loadAll, setBusy,
      onDone: async () => {
        await loadAll();
        setCurrentTab('vendor');
      },
    }));
  }

  async function handleDeleteDraft(opo) {
    if (!window.confirm(`Delete draft ${opo.opo_number}? Its tasks return to the staging pool.`)) return;
    const who = await cui.actor('edit');
    if (!who) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/outside_processing_orders/${opo.opo_id}`, who.pin
        ? { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(who) }
        : { method: 'DELETE' });
      if (!res.ok) {
        window.alert(await parseServerError(res));
        return;
      }
      await loadAll();
    } catch (err) {
      window.alert(err.message || 'Failed to delete the draft.');
    } finally {
      setBusy(false);
    }
  }

  function handlePreviewPdf(opo) {
    openPdf(`${API_BASE}/outside_processing_orders/${opo.opo_id}/pdf`);
  }

  /** One 4x6 label per pallet on the manifest (coatingActions.printPalletLabels). */
  async function handlePalletLabels(opo) {
    await printPalletLabels({ apiBase: API_BASE, cui, opo });
  }

  function openInvoiceModal(opo) {
    setInvoiceOpo(opo);
    setInvoiceCost(opo.actual_cost != null ? String(opo.actual_cost)
                   : (opo.estimated_cost != null ? String(opo.estimated_cost) : ''));
    setInvoiceFreight(opo.freight_cost != null && Number(opo.freight_cost) > 0
                      ? String(opo.freight_cost) : '');
    setInvoiceNumber(opo.vendor_invoice_number || '');
  }

  async function handleSaveInvoice() {
    const opo = invoiceOpo;
    if (!opo) return;
    const cost = parseFloat(invoiceCost);
    if (isNaN(cost) || cost < 0) {
      alert('Enter the invoiced cost from the vendor.');
      return;
    }
    const body = { actual_cost: cost };
    if (invoiceFreight !== '' && !isNaN(parseFloat(invoiceFreight))) {
      body.freight_cost = parseFloat(invoiceFreight);
    }
    if (invoiceNumber.trim()) body.vendor_invoice_number = invoiceNumber.trim();
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/outside_processing_orders/${opo.opo_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(detailToString(err.detail) || `Save failed (${res.status})`);
      }
      setInvoiceOpo(null);
      await loadAll();
    } catch (e) {
      alert(e.message || 'Could not save the invoice.');
    } finally {
      setBusy(false);
    }
  }

  /** "Vendor signing": the paper's QR (the same as its printed copy), or who signed for it at the vendor. */
  async function handleVendorSigning(opo) {
    const { vendorSigningPrompt } = await import('../features/signing/VendorSigningPanel');
    const vendorUi = {
      // who: the hub's Working-as session (operator mode), else a typed PIN (never the admin session's)
      pin: (m) => (operator && ui && ui.pin ? ui.pin(m) : pinPrompt(m, { autofill: false })),
      confirm: async (text) => window.confirm(text),
    };
    await vendorSigningPrompt({ apiBase: API_BASE, ui: vendorUi, opo, onPrint: () => handleReprint(opo) });
    await loadAll();
  }

  async function handleReprint(opo) {
    await reprintPaper({ apiBase: API_BASE, cui, opo });
  }

  async function handleEmail(opo) {
    const vendorLabel = opo.vendor_name || 'the vendor';
    if (!window.confirm(`Email ${opo.opo_number} to ${vendorLabel}?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/outside_processing_orders/${opo.opo_id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...emailSenderHeaders() },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        window.alert(await parseServerError(res));
        return;
      }
      const data = await res.json().catch(() => ({}));
      window.alert(data.message || 'Manifest emailed.');
    } catch (err) {
      window.alert(err.message || 'Emailing the manifest failed.');
    } finally {
      setBusy(false);
    }
  }

  function openReceiveModal(opo) {
    setReceiveRows(receiveRowsFor(opo));
    // Prefill from the committed estimate rather than blanking (coatingData.receiveOfficePrefill). Once Returned
    // there is no UI path back to these fields but Enter invoice.
    const pre = receiveOfficePrefill(opo);
    setReceiveInvoice(pre.invoice);
    setReceiveCost(pre.cost);
    setReceiveFreight(pre.freight);
    setReceiveOpo(opo);
  }

  async function handleReceiveSubmit() {
    const opo = receiveOpo;
    if (!opo) return;
    await receiveManifest({
      apiBase: API_BASE, cui, opo, rows: receiveRows, office,
      invoice: receiveInvoice, cost: receiveCost, freight: receiveFreight, setBusy,
      onDone: async () => {
        setReceiveOpo(null);
        await loadAll();
      },
    });
  }

  // ---- Render helpers --------------------------------------------------------

  function soBadge(opo) {
    return opo.sales_order_number ? (
      <span className="badge bg-secondary">SO {opo.sales_order_number}</span>
    ) : (
      <span className="badge bg-light text-dark border">Internal</span>
    );
  }

  function statusBadge(status) {
    const cls =
      status === 'Draft'
        ? 'bg-light text-dark border'
        : status === 'Sent'
        ? 'bg-primary'
        : status === 'Partially Returned'
        ? 'bg-warning text-dark'
        : status === 'Returned'
        ? 'bg-success'
        : status === 'Closed'
        ? 'bg-secondary'
        : 'bg-danger'; // Cancelled
    return <span className={`badge ${cls}`}>{status}</span>;
  }

  function lineIdentity(l) {
    // Assembly identity leads on manifest lines too. Legacy per-task OPO lines
    // (pre-2026-08-12) were written part-first ("FAB311916 - 17x8.33 plate"),
    // which read as a bare component even though the batch is WLD304072
    // assemblies — the include_lines payload carries line_assembly_name and
    // master_job_num so the UI can say what the coater actually has.
    const id = lineIdentityOf(l);
    return (
      <>
        {id.assembly ? (
          <>
            <span className="badge bg-success me-1" style={{ fontSize: '0.65em', verticalAlign: 'middle' }}>ASSY</span>
            <span className="fw-semibold">{id.assembly}</span>
            {' — '}
          </>
        ) : null}
        {id.text}
        {id.job ? (
          <span className="text-muted"> · Job {id.job}</span>
        ) : null}
      </>
    );
  }

  function renderPartCell(productId, description, imagePath, assemblyName) {
    // Assembly identity leads. The coating step often lives on the ASSEMBLY's
    // operations (assembly_processes) — e.g. WLD304072 coats at SJS while its
    // primary part FAB311916 is routed Laser,Brake. Showing only the primary
    // part makes a correct row read as "this part doesn't even have coating".
    return (
      <div className="d-flex align-items-center gap-2">
        {imagePath ? (
          <img
            src={imagePath}
            alt={assemblyName || productId || 'Part'}
            className="border"
            style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }}
          />
        ) : null}
        <div>
          {assemblyName ? (
            <>
              <div className="fw-bold">
                <span className="badge bg-success me-1" style={{ fontSize: '0.65em', verticalAlign: 'middle' }}>ASSY</span>
                {assemblyName}
              </div>
              <div className="small text-muted">
                {productId}{description ? ` — ${description}` : ''}
              </div>
            </>
          ) : (
            <>
              <div className="fw-bold">{productId || '—'}</div>
              {description ? <div className="small text-muted">{description}</div> : null}
            </>
          )}
        </div>
      </div>
    );
  }

  /** A job split by allocation, listed under the order its pieces are packed for: no checkbox
   *  (they go on this order's paper from its pallet button), this order's count only. */
  function renderShareRow(t, a) {
    const age = ageBadge(t.staged_at);
    const homeSo = t.sales_order_number || 'another order';
    return (
      <tr key={`${t.task_id}-so${a.sales_order_id}`} data-testid={`staging-share-${t.task_id}-${a.sales_order_id}`}>
        <td style={{ width: 34 }} />
        <td>
          {renderPartCell(t.product_id, t.part_description, t.image_path, t.assembly_name)}
          {mixedProcesses && t.process_label && (
            <span className="badge bg-secondary ms-2 align-middle">{t.process_label}</span>
          )}
        </td>
        <td>
          <span className="fw-semibold">#{t.master_job_num}</span>
          <span
            className="badge bg-warning text-dark ms-2"
            title={`Cut on ${homeSo}'s job #${t.master_job_num}: these pieces are packed on this order's pallet and go on this order's paper`}
          >
            ↪ from {homeSo}
          </span>
        </td>
        <td>
          {fmtQty(a.qty)}
          {Number(a.sent) > 0 ? (
            <div className="small text-warning" style={{ whiteSpace: 'nowrap' }}>
              {fmtQty(a.remaining)} left ({fmtQty(a.sent)} already on papers)
            </div>
          ) : null}
          <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
            packed for this order, on no pallet yet
          </div>
        </td>
        {renderTrailingCells(t, age)}
      </tr>
    );
  }

  function renderTrailingCells(t, age) {
    return (
      <>
        <td>
          {t.color_code ? (
            <CoatingSwatch code={t.color_code} name={t.color_name} hex={t.color_hex} />
          ) : (
            <span className="small" style={{ color: 'var(--jq-pink, #d63384)' }}>
              not set
            </span>
          )}
        </td>
        <td>
          {t.vendor_name ? (
            <span className="small">{t.vendor_name}</span>
          ) : (
            <span className="badge bg-warning text-dark" style={{ fontWeight: 500 }}>
              no vendor — set on part or pick at create
            </span>
          )}
        </td>
        <td className="small">{fmtDate(t.due_date)}</td>
        <td>
          <span className={`badge ${age.className}`}>{age.label}</span>
        </td>
      </>
    );
  }

  function renderStagingRow(t) {
    if (t._share) return renderShareRow(t, t._share);
    const age = ageBadge(t.staged_at);
    // A job split by allocation (jobq-api `allocated_away`): this row's order has nothing left
    // to put on paper; the rest goes on the other order's paper from ITS pallet button.
    const shareDone = ownShareDone(t);
    return (
      <tr key={t.task_id}>
        <td style={{ width: 34 }}>
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedTaskIds.has(t.task_id)}
            disabled={shareDone && !selectedTaskIds.has(t.task_id)}
            title={shareDone
              ? `Nothing of it is left for this order. The rest is packed for ${allocatedAwayText(t)}: use that order's pallet button.`
              : undefined}
            onChange={() => toggleTask(t)}
          />
        </td>
        <td>
          {renderPartCell(t.product_id, t.part_description, t.image_path, t.assembly_name)}
          {/* WHICH operation. The board has always selected through the general resolver,
              so the moment a second process is outsourceable this queue mixes (say) brake
              and coating tasks. Without this they look identical, and one manifest could
              send brake parts to a powder coater. Hidden while every staged task is the
              same process, so today's screen is unchanged. */}
          {mixedProcesses && t.process_label && (
            <span className="badge bg-secondary ms-2 align-middle">{t.process_label}</span>
          )}
        </td>
        <td>
          <span className="fw-semibold">#{t.master_job_num}</span>
          {t.set_identifier ? (
            <span
              className="ms-2"
              style={{
                color: 'var(--jq-set-chip, #fd7e14)',
                border: '1px solid var(--jq-set-chip, #fd7e14)',
                borderRadius: 4,
                padding: '0 5px',
                fontSize: '0.75em',
                whiteSpace: 'nowrap',
              }}
            >
              SET {t.set_identifier}
            </span>
          ) : null}
          {t.cross_so_allocated ? (
            <span
              className="badge bg-warning text-dark ms-2"
              title="Cut on another SO's job — this part's output is surplus-allocated to the SO shown here"
            >
              ↪ alloc
            </span>
          ) : null}
        </td>
        <td title={`${fmtQty(t.quantity)} task unit(s)`}>
          {fmtQty(t.units != null ? t.units : t.quantity)}
          {t.assembly_units ? <span className="small text-muted ms-1">assemblies</span> : null}
        
          {Array.isArray(t.allocated_away) && t.allocated_away.length > 0 ? (
            <>
              <div className="small text-warning" style={{ whiteSpace: 'nowrap' }}>
                {fmtQty(t.units_remaining)} left for {t.sales_order_number || 'this order'}
                {Number(t.batched_qty) > 0 ? ` (${fmtQty(t.batched_qty)} already on papers)` : ''}
              </div>
              {t.allocated_away.map((a) => (
                <div
                  key={a.sales_order_id}
                  className="small text-muted"
                  style={{ whiteSpace: 'nowrap' }}
                  title={`Packed for ${a.sales_order_number || `SO #${a.sales_order_id}`}: these pieces ride that order's pallet and go on its paper`}
                >
                  ↪ {fmtQty(a.qty)} for {a.sales_order_number || `SO #${a.sales_order_id}`}
                  {(a.pallets || []).length ? ` on ${a.pallets.join(', ')}` : ''}
                  {' '}({Number(a.remaining) <= 0 ? 'on its paper'
                    : a.sales_order_id != null ? `listed under ${a.sales_order_number || 'that order'}`
                      : 'its pallet button'})
                </div>
              ))}
            </>
          ) : t.units_remaining != null ? (
            <div className="small text-warning" style={{ whiteSpace: 'nowrap' }}>
              {fmtQty(t.units_remaining)} left
              {Number(t.batched_qty) > 0 ? ` (${fmtQty(t.batched_qty)} already on papers)` : ''}
            </div>
          ) : null}
          {Number(t.uncoated_extras) > 0 ? (
            <div
              className="small text-muted"
              style={{ whiteSpace: 'nowrap' }}
              title="Cut beyond the order. The order's yield policy doesn't ship extras, so they don't go out; the task completes without them."
            >
              {fmtQty(t.uncoated_extras)} extra{Number(t.uncoated_extras) === 1 ? '' : 's'} stay home (order doesn't ship extras)
            </div>
          ) : null}
          {t.cross_so_allocated && t.allocated_qty != null && !t.assembly_units
            && Number(t.allocated_qty) < Number(t.units || t.quantity) ? (
            <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
              {fmtQty(t.allocated_qty)} for this SO · {fmtQty(Number(t.units || t.quantity) - Number(t.allocated_qty))} surplus
            </div>
          ) : null}
        </td>
        {renderTrailingCells(t, age)}
      </tr>
    );
  }

  // Only badge the process when the queue actually holds more than one (coatingData.hasMixedProcesses).
  const mixedProcesses = hasMixedProcesses(stagingGroups);


  /** One job's line under a pallet's part: job #, its count on this pallet, and why it differs from the job. */
  function renderJobLine(jobLine, i) {
    const { t, qty, share } = jobLine;
    const homeSo = t.sales_order_number || 'another order';
    const notes = jobLineNotes(jobLine);
    return (
      <div key={`${t.task_id}-${i}`} className="small d-flex flex-wrap align-items-baseline" style={{ columnGap: 6 }}
        data-testid={share ? `staging-share-${t.task_id}-${share.sales_order_id}` : `staging-job-${t.task_id}`}>
        <span className="fw-semibold">#{t.master_job_num}</span>
        {t.set_identifier ? (
          <span style={{ color: 'var(--jq-set-chip, #fd7e14)', border: '1px solid var(--jq-set-chip, #fd7e14)',
            borderRadius: 4, padding: '0 4px', fontSize: '0.85em', whiteSpace: 'nowrap' }}>
            SET {t.set_identifier}
          </span>
        ) : null}
        {share ? (
          <span className="badge bg-warning text-dark"
            title={`Cut on ${homeSo}'s job #${t.master_job_num}: these pieces are packed on this pallet and go on its paper`}>
            ↪ from {homeSo}
          </span>
        ) : null}
        <span className="text-muted">{fmtQty(qty)}</span>
        {notes.length ? <span className="text-warning">({notes.join(' · ')})</span> : null}
      </div>
    );
  }

  /** A pallet: header (tick, PLT, Find, total), then a row per part with its total and the jobs that make it. */
  function renderPalletBucket(g, buckets, p) {
    const otherSo = isOtherOrderPallet(p.palletOrder, g.sales_order_id);
    const state = palletTickState(p, selectedTaskIds);
    const linked = linkedPalletNames(buckets, p);
    const anyAssembly = p.parts.some((x) => x.sample.assembly_units);
    const packedTotal = palletPackedTotal(p);
    return (
      <React.Fragment key={`${g.key}-${p.key}`}>
        <tr data-testid={`staging-pallet-${p.short_id}`} style={{ backgroundColor: 'var(--jq-bg-subtle, #f1f3f5)' }}>
          <td>
            {p.riderOnly ? null : (
              <input
                type="checkbox"
                className="form-check-input"
                aria-label={`Tick pallet ${p.short_id}`}
                title={`Tick ${p.short_id}: its paper carries everything packed on it`}
                checked={state === 'all'}
                ref={(el) => { if (el) el.indeterminate = state === 'some'; }}
                onChange={(e) => togglePallet(g, buckets.pallets, p.key, e.target.checked)}
              />
            )}
          </td>
          <td colSpan={7}>
            <span
              className={otherSo ? 'badge bg-light text-dark border border-dark' : 'badge bg-dark'}
              style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
            >
              {p.short_id}
            </span>
            {otherSo ? (
              <span className="small text-muted ms-1">{p.palletOrder.sales_order_number}'s pallet</span>
            ) : null}
            {p.pallet_id ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-2"
                style={{ fontSize: '0.8em' }}
                title={`Light ${p.short_id}'s beacon and show where it is`}
                data-testid={`staging-find-${p.pallet_id}`}
                onClick={() => runPalletFlow(() => findPallets([p.pallet_id], `Find ${p.short_id}`))}
              >
                Find
              </button>
            ) : null}
            <span className="ms-3 fw-semibold" data-testid={`staging-pallet-total-${p.short_id}`}>
              {fmtQty(p.total)}{anyAssembly ? '' : ' pcs'}
            </span>
            {packedTotal - p.total > 1e-9 ? (
              <span className="small text-muted ms-1">to send ({fmtQty(packedTotal)} on the pallet, the rest already on papers)</span>
            ) : null}
            <span className="small text-muted ms-1">
              · {p.parts.length} part{p.parts.length === 1 ? '' : 's'}
            </span>
            {linked.length ? (
              <span className="small text-muted ms-2" title="A job on this pallet is also on these: one paper carries all of it">
                · ticked with {linked.join(', ')} (a job is on both)
              </span>
            ) : null}
            {p.riderOnly ? (
              <>
                <span className="small text-muted ms-2">only other orders' pieces are left on it</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-dark py-0 px-2 ms-2"
                  style={{ fontSize: '0.75em' }}
                  title={otherSo
                    ? `${p.short_id} is ${p.palletOrder.sales_order_number}'s pallet: this makes ${p.palletOrder.sales_order_number}'s paper`
                    : `One paper for ${p.short_id}, exactly what the packing plan puts on it`}
                  disabled={busy}
                  onClick={() => createPalletPaper(p.short_id)}
                >
                  Make {p.short_id}'s paper{otherSo ? ` (${p.palletOrder.sales_order_number})` : ''}
                </button>
              </>
            ) : null}
          </td>
        </tr>
        {p.parts.map((part) => {
          const ts = part.lines.map((l) => l.t);
          const vendors = new Set(ts.map((t) => t.vendor_id ?? null));
          const shown = {
            ...part.sample,
            vendor_name: vendors.size > 1 ? 'Mixed defaults' : part.sample.vendor_name,
            due_date: earliest(ts.map((t) => t.due_date)),
          };
          return (
            <tr key={`${g.key}-${p.key}-${part.key}`} data-testid={`staging-part-${p.short_id}-${part.sample.product_id}`}>
              <td />
              <td style={{ paddingLeft: 20 }}>
                {renderPartCell(part.sample.product_id, part.sample.part_description, part.sample.image_path,
                  part.sample.assembly_name)}
                {mixedProcesses && part.sample.process_label && (
                  <span className="badge bg-secondary ms-2 align-middle">{part.sample.process_label}</span>
                )}
              </td>
              <td>{part.lines.map(renderJobLine)}</td>
              <td>
                <span className="fw-bold" style={{ fontSize: '1.1em' }} data-testid="staging-part-total">
                  {fmtQty(part.total)}
                </span>
                {part.lines.length > 1 ? (
                  <div className="small text-muted">{part.lines.length} jobs</div>
                ) : null}
              </td>
              {renderTrailingCells(shown, ageBadge(earliest(ts.map((t) => t.staged_at))))}
            </tr>
          );
        })}
      </React.Fragment>
    );
  }

  function renderStagingTab() {
    return (
      <>
        {stagingGroups.length === 0 && !loading && (
          <div className="alert alert-info">
            No outsourced tasks are staged for sending. Unblocked tasks for any outsourced
            process appear here as upstream stations finish.
          </div>
        )}
        {stagingGroups.length > 0 && (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 34 }}></th>
                  <th>Part</th>
                  <th>Jobs</th>
                  <th>Qty</th>
                  <th>Color</th>
                  <th>Default Vendor</th>
                  <th>Due</th>
                  <th>Staged</th>
                </tr>
              </thead>
              <tbody>
                {stagingGroups.map((g) => {
                  const { selectable, allSelected } = groupSelection(g, selectedTaskIds);
                  const ownCount = g.tasks.filter((t) => !t._share).length;
                  const shareCount = g.tasks.length - ownCount;
                  const buckets = bucketsByGroup.get(g.key) || { pallets: [], loose: [] };
                  return (
                    <React.Fragment key={g.key}>
                      <tr style={{ backgroundColor: 'var(--jq-bg-surface, #f8f9fa)' }}>
                        <td>
                          {/* the title also marks a group header for the tests: keep it on a hidden box */}
                          <input
                            type="checkbox"
                            className="form-check-input"
                            title="Select all in this group"
                            checked={allSelected}
                            style={selectable.length ? undefined : { visibility: 'hidden' }}
                            disabled={!selectable.length}
                            onChange={(e) => toggleGroup(g, e.target.checked)}
                          />
                        </td>
                        <td colSpan={7}>
                          {g.sales_order_number ? (
                            <>
                              <span className="badge bg-secondary me-2">SO {g.sales_order_number}</span>
                              <span className="fw-bold">{g.customer_name || ''}</span>
                            </>
                          ) : (
                            <span className="fw-bold">
                              <span className="badge bg-light text-dark border me-2">Internal</span>
                              Internal — {g.customer_name || 'no customer'}
                            </span>
                          )}
                          <span className="small text-muted ms-2">
                            {[
                              buckets.pallets.length
                                ? `${buckets.pallets.length} pallet${buckets.pallets.length !== 1 ? 's' : ''}` : null,
                              ownCount ? `${ownCount} task${ownCount !== 1 ? 's' : ''}` : null,
                              shareCount ? `${shareCount} cut on another order's job` : null,
                            ].filter(Boolean).join(' · ')}
                          </span>
                          {(() => {
                            // Find all (2026-10-27): every pallet this order's staged tasks sit on, before any
                            // manifest exists (another order's pallet holding its pieces included)
                            const ids = groupPalletIds(g);
                            if (!ids.length) return null;
                            const who = g.sales_order_number || g.customer_name || 'these tasks';
                            return (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary py-0 px-2 ms-2"
                                style={{ fontSize: '0.75em' }}
                                title={`Light the beacon on each of ${who}'s ${ids.length} pallet${ids.length === 1 ? '' : 's'} and show where they are`}
                                data-testid={`staging-find-all-${g.key}`}
                                onClick={() => runPalletFlow(() => findPallets(ids, `Find the pallets: ${who}`))}
                              >
                                Find all ({ids.length})
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                      {buckets.pallets.map((p) => renderPalletBucket(g, buckets, p))}
                      {buckets.loose.length && buckets.pallets.length ? (
                        <tr>
                          <td />
                          <td colSpan={7} className="small text-muted fst-italic">Not on a pallet yet</td>
                        </tr>
                      ) : null}
                      {buckets.loose.map((t) => renderStagingRow(t))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Forward look: blocked coating tasks still in production upstream. */}
        <details className="mt-3">
          <summary className="fw-semibold" style={{ cursor: 'pointer' }}>
            In production ({board.in_production.length})
            <span className="small text-muted ms-2">
              — blocked coating tasks upstream, so the office sees what is coming
            </span>
          </summary>
          {board.in_production.length === 0 ? (
            <div className="small text-muted mt-2">Nothing upstream right now.</div>
          ) : (
            <div className="table-responsive mt-2">
              <table className="table table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Part</th>
                    <th>Job #</th>
                    <th>Qty</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {board.in_production.map((t) => (
                    <tr key={t.task_id}>
                      <td>{renderPartCell(t.product_id, t.part_description, t.image_path, t.assembly_name)}</td>
                      <td>
                        #{t.master_job_num}
                        {t.set_identifier ? (
                          <span className="small text-muted ms-1">(SET {t.set_identifier})</span>
                        ) : null}
                      </td>
                      <td>{fmtQty(t.quantity)}</td>
                      <td className="small">{fmtDate(t.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>

        {/* Sticky batch bar */}
        {selectedTaskIds.size > 0 && (
          <div
            className="border-top d-flex flex-wrap align-items-center gap-2 p-2"
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 50,
              backgroundColor: 'var(--jq-bg-body, #fff)',
            }}
          >
            <span className="fw-bold me-2">{selectedTaskIds.size} selected</span>
            {(() => {
              // what the paper will carry, pallet by pallet: the number to check each pallet against
              const ticked = tickedPallets(stagingGroups, bucketsByGroup, selectedTaskIds);
              if (!ticked.length) return null;
              return (
                <span className="small text-muted me-2" data-testid="staging-ticked-pallets">
                  {ticked.map((p) => `${p.short_id} ${fmtQty(p.total)}`).join(' · ')}
                </span>
              );
            })()}
            <button className="btn btn-primary" disabled={busy} onClick={openCreateModal}>
              Create Manifest ({selectedTaskIds.size})
            </button>
            <div className="d-flex align-items-center gap-2 ms-2">
              <button
                className="btn btn-outline-primary"
                disabled={busy || matchingDrafts.length === 0 || !addToDraftId}
                title={
                  matchingDrafts.length === 0
                    ? 'No Draft manifest matches this selection’s SO and vendor'
                    : 'Add the selected tasks to an existing draft manifest'
                }
                onClick={handleAddToDraft}
              >
                Add to Draft…
              </button>
              {matchingDrafts.length > 0 && (
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={addToDraftId}
                  onChange={(e) => setAddToDraftId(e.target.value)}
                >
                  {matchingDrafts.map((o) => (
                    <option key={o.opo_id} value={String(o.opo_id)}>
                      {o.opo_number} · {o.vendor_name || 'no vendor'} · {o.line_count} line
                      {o.line_count !== 1 ? 's' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <button
              className="btn btn-sm btn-link text-muted ms-auto"
              onClick={() => setSelectedTaskIds(new Set())}
            >
              Clear selection
            </button>
          </div>
        )}
      </>
    );
  }

  function renderDraftsTab() {
    if (!draftManifests.length && !loading) {
      return (
        <div className="alert alert-info">
          No draft manifests. Select staged tasks on the Staging tab and Create Manifest to
          start one.
        </div>
      );
    }
    return draftManifests.map((opo) => {
      const deadLines = (opo.lines || []).filter(isDeadLine);
      return (
        <div className="card mb-3" key={opo.opo_id}>
          <div className="card-header d-flex flex-wrap align-items-center gap-2">
            <span className="fw-bold" style={{ fontFamily: 'monospace' }}>
              {opo.opo_number}
            </span>
            {soBadge(opo)}
            <span>{opo.vendor_name || <span className="text-muted">no vendor</span>}</span>
            <span className="ms-auto fw-semibold">{fmtMoney(opo.estimated_total)}</span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 34 }}>#</th>
                    <th>Part / Job</th>
                    <th>Qty sent</th>
                    <th>Color</th>
                    <th className="text-end">Unit $</th>
                    <th className="text-end">Line $</th>
                    <th style={{ width: 44 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(opo.lines || []).map((l) => {
                    const dead = isDeadLine(l);
                    return (
                      <tr key={l.opo_line_id} className={dead ? 'table-danger' : ''}>
                        <td>{l.line_number}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {l.part_image_path ? (
                              <img
                                src={l.part_image_path}
                                alt={l.part_product_id || 'Part'}
                                className="border"
                                style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
                              />
                            ) : null}
                            <div>
                              <div className="small fw-semibold">{lineIdentity(l)}</div>
                              {dead && (
                                <div className="small text-danger">
                                  task {String(l.task_status || '').toLowerCase()} — remove this line before sending
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{fmtQty(l.quantity_sent)}</td>
                        <td>
                          {l.color_code ? (
                            <CoatingSwatch code={l.color_code} name={l.color_name} hex={l.color_hex} />
                          ) : (
                            <span className="small" style={{ color: 'var(--jq-pink, #d63384)' }}>
                              not set
                            </span>
                          )}
                        </td>
                        <td className="text-end">{fmtMoney(l.unit_cost)}</td>
                        <td className="text-end">{fmtMoney(l.line_cost)}</td>
                        <td className="text-end">
                          {l.job_task_id ? (
                            <button
                              className="btn btn-sm btn-outline-danger py-0 px-1"
                              title="Remove line — the task returns to staging"
                              disabled={busy}
                              onClick={() => handleRemoveLine(opo, l)}
                            >
                              ✕
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer d-flex flex-wrap align-items-center gap-2">
            <button
              className="btn btn-success"
              disabled={busy}
              title={
                deadLines.length
                  ? 'Remove voided/cancelled task lines before sending'
                  : `Send ${opo.opo_number} to the vendor`
              }
              onClick={() => handleSendManifest(opo)}
            >
              Send
            </button>
            <button className="btn btn-outline-secondary" disabled={busy} onClick={() => handlePreviewPdf(opo)}>
              Preview PDF
            </button>
            <button
              className="btn btn-outline-secondary"
              disabled={busy}
              title="Print a 4x6 label for each pallet on this manifest"
              onClick={() => handlePalletLabels(opo)}
            >
              Pallet labels
            </button>
            <button
              className="btn btn-outline-primary"
              disabled={busy}
              title="List the pallets on this manifest and light their beacons"
              onClick={() => runPalletFlow(() => showPallets(opo.opo_id, 'to_vendor'))}
            >
              Find pallets
            </button>
            {office && (
              <button
                className="btn btn-outline-danger ms-auto"
                disabled={busy}
                onClick={() => handleDeleteDraft(opo)}
              >
                Delete draft
              </button>
            )}
          </div>
        </div>
      );
    });
  }

  function renderAtVendorTab() {
    if (!atVendorOpos.length && !loading) {
      return <div className="alert alert-info">Nothing is out at a vendor right now.</div>;
    }
    return atVendorOpos.map((opo) => {
      const { daysOut, pastDue } = atVendorFacts(opo);
      const manifest = isManifest(opo);
      const vendorChip = vendorSignChip(opo, { on: vendorOn });
      return (
        <div className="card mb-3" key={opo.opo_id}>
          <div className="card-header d-flex flex-wrap align-items-center gap-2">
            <span className="fw-bold" style={{ fontFamily: 'monospace' }}>
              {opo.opo_number}
            </span>
            {statusBadge(opo.status)}
            {soBadge(opo)}
            <span>{opo.vendor_name || <span className="text-muted">no vendor</span>}</span>
            {!manifest && (
              <span className="badge bg-light text-dark border" title="No task-linked lines — receiving records quantities only">
                manual OPO
              </span>
            )}
            {vendorChip && (
              <span className={`badge ${vendorChip.tone === 'success' ? 'bg-success' : vendorChip.tone === 'warning' ? 'bg-warning text-dark' : 'bg-secondary'}`}
                data-testid="vendor-sign-chip">
                {vendorChip.text}
              </span>
            )}
            {vendorChip && vendorChip.problem && <span className="badge bg-danger">Problem noted</span>}
            {daysOut != null && (
              <span className={`badge ms-auto ${pastDue ? 'bg-danger' : 'bg-light text-dark'}`}>
                {daysOut} d at vendor
              </span>
            )}
          </div>
          <div className="card-body py-2">
            <div className="d-flex flex-wrap gap-3 small mb-2">
              <span>
                <span className="text-muted">Sent:</span> {fmtDate(opo.sent_at)}
              </span>
              <span className={pastDue ? 'text-danger fw-semibold' : ''}>
                <span className="text-muted">Due back:</span> {fmtDate(opo.due_back)}
              </span>
              <span>
                <span className="text-muted">Units at vendor:</span> {fmtQty(opo.units_at_vendor)}
              </span>
              {opo.color_summary ? (
                <span>
                  <span className="text-muted">Color:</span> {opo.color_summary}
                </span>
              ) : null}
            </div>
            {(opo.lines || []).length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Part / Job</th>
                      <th>Color</th>
                      <th className="text-end">Returned / Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(opo.lines || []).map((l) => {
                      const done = lineBack(l);
                      return (
                        <tr key={l.opo_line_id}>
                          <td>{l.line_number}</td>
                          <td className="small">{lineIdentity(l)}</td>
                          <td>
                            {l.color_code ? (
                              <CoatingSwatch code={l.color_code} name={l.color_name} hex={l.color_hex} />
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td className="text-end small">
                            {fmtQty(done)} / {fmtQty(l.quantity_sent)}
                            {Number(l.quantity_scrapped || 0) > 0 && (
                              <span className="text-danger ms-1">
                                ({fmtQty(l.quantity_scrapped)} scrap)
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="card-footer d-flex flex-wrap gap-2">
            <button className="btn btn-success" disabled={busy} onClick={() => openReceiveModal(opo)}>
              Receive…
            </button>
            <button className="btn btn-outline-secondary" disabled={busy} onClick={() => handleReprint(opo)}>
              Reprint
            </button>
            <button
              className="btn btn-outline-secondary"
              disabled={busy}
              title="Print a 4x6 label for each pallet on this manifest"
              onClick={() => handlePalletLabels(opo)}
            >
              Pallet labels
            </button>
            {vendorOn && (
              <button
                className="btn btn-outline-primary"
                disabled={busy}
                title="The QR the vendor scans to sign for the parts (the same as on the printed paper), or who signed"
                onClick={() => handleVendorSigning(opo)}
              >
                {vendorChip && vendorChip.state === 'signed' ? 'Vendor signature' : 'Vendor signing QR'}
              </button>
            )}
            <button
              className="btn btn-outline-primary"
              disabled={busy}
              title="Pallets back from the vendor: light their beacons on the shelf and record them back on"
              onClick={() => handleBeaconsBackOn(opo)}
            >
              Beacons back on
            </button>
            {office && (
              <button className="btn btn-outline-secondary" disabled={busy} onClick={() => handleEmail(opo)}>
                Email
              </button>
            )}
          </div>
        </div>
      );
    });
  }

  function renderInvoiceModal() {
    if (!invoiceOpo) return null;
    return (
      <>
        <div className="modal show d-block" tabIndex={-1} style={{ zIndex: 1055 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Vendor invoice — <span style={{ fontFamily: 'monospace' }}>{invoiceOpo.opo_number}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setInvoiceOpo(null)} />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  {invoiceOpo.vendor_name || 'Vendor'} · estimated {fmtMoney(invoiceOpo.estimated_total)}.
                  Until the invoiced amount is recorded, the sales order accrues the estimate.
                </p>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1">Invoiced cost $</label>
                    <input
                      type="number" className="form-control form-control-sm" min={0} step="0.01"
                      value={invoiceCost} onChange={(e) => setInvoiceCost(e.target.value)} autoFocus
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1">Freight $ (optional)</label>
                    <input
                      type="number" className="form-control form-control-sm" min={0} step="0.01"
                      value={invoiceFreight} onChange={(e) => setInvoiceFreight(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold mb-1">Vendor invoice # (optional)</label>
                    <input
                      type="text" className="form-control form-control-sm"
                      value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setInvoiceOpo(null)} disabled={busy}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveInvoice} disabled={busy}>
                  {busy ? 'Saving…' : 'Save invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop show" style={{ zIndex: 1050 }} />
      </>
    );
  }

  function renderHistoryTab() {
    if (!historyOpos.length && !loading) {
      return <div className="alert alert-info">No returned, closed or cancelled manifests yet.</div>;
    }
    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>Manifest</th>
              <th>Status</th>
              <th>Vendor</th>
              <th>SO</th>
              <th>Returned</th>
              <th className="text-end">Cost</th>
              <th>Invoice #</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {historyOpos.map((opo) => (
              <tr key={opo.opo_id}>
                <td>
                  <span className="fw-bold" style={{ fontFamily: 'monospace' }}>
                    {opo.opo_number}
                  </span>
                  {!isManifest(opo) && (
                    <span className="badge bg-light text-dark border ms-2">manual OPO</span>
                  )}
                </td>
                <td>{statusBadge(opo.status)}</td>
                <td className="small">{opo.vendor_name || '—'}</td>
                <td>{soBadge(opo)}</td>
                <td className="small">{fmtDate(opo.returned_at)}</td>
                <td className="text-end">
                  {opo.actual_total != null ? (
                    fmtMoney(opo.actual_total)
                  ) : (
                    <span title="No vendor invoice recorded — estimated total">
                      {fmtMoney(opo.estimated_total)} <span className="small text-muted">(est)</span>
                    </span>
                  )}
                </td>
                <td className="small">{opo.vendor_invoice_number || '—'}</td>
                <td className="text-end">
                  {office && opo.status !== 'Cancelled' && (
                    <button
                      className={`btn btn-sm me-1 ${opo.actual_total != null ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                      disabled={busy}
                      title={opo.actual_total != null
                        ? 'Correct the recorded vendor invoice'
                        : 'No vendor invoice recorded — the sales order is still accruing the estimate'}
                      onClick={() => openInvoiceModal(opo)}
                    >
                      {opo.actual_total != null ? 'Edit invoice' : 'Enter invoice'}
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={busy}
                    onClick={() => handleReprint(opo)}
                  >
                    Reprint
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ---- Modals ----------------------------------------------------------------

  function renderCreateModal() {
    if (!showCreateModal) return null;
    return (
      <>
        <div className="modal show d-block" tabIndex={-1} style={{ zIndex: 1055 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Manifest ({selectedTaskIds.size} task{selectedTaskIds.size !== 1 ? 's' : ''})</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Vendor <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={createVendorId}
                    onChange={(e) => setCreateVendorId(e.target.value)}
                  >
                    <option value="">-- pick the coater --</option>
                    {serviceVendors.map((v) => (
                      <option key={v.vendor_id} value={String(v.vendor_id)}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                  {selectionVendorId != null && (
                    <div className="form-text">Pre-filled from the selected parts’ default vendor. One manifest is one VENDOR — processes may mix on it (one truck can carry tube laser and press brake work).</div>
                  )}
                  {(() => {
                    // Defaults are advisory, not locks — tell the office which
                    // selected tasks normally go elsewhere before they commit.
                    const over = vendorOverride(selectedTasks, createVendorId, serviceVendors);
                    if (!over) return null;
                    return (
                      <div className="alert alert-warning py-2 px-3 mt-2 mb-0 small">
                        {over.count} task{over.count !== 1 ? 's' : ''} default
                        {over.count === 1 ? 's' : ''} to {over.names.join(', ') || 'another vendor'} —
                        this manifest sends {over.count === 1 ? 'it' : 'them'} to{' '}
                        {over.chosenName} instead.
                      </div>
                    );
                  })()}
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Due back (optional)</label>
                  <input
                    type="date"
                    className="form-control"
                    value={createDueBack}
                    onChange={(e) => setCreateDueBack(e.target.value)}
                  />
                </div>
                <div className="mb-1">
                  <label className="form-label fw-semibold">Notes to vendor (optional)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={createNotes}
                    onChange={(e) => setCreateNotes(e.target.value)}
                    placeholder="Prints on the manifest for the coater"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" disabled={busy} onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" disabled={busy || !createVendorId} onClick={handleCreateManifest}>
                  {busy ? 'Creating…' : 'Create Manifest'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop show" style={{ zIndex: 1050 }}></div>
      </>
    );
  }

  function renderReceiveModal() {
    if (!receiveOpo) return null;
    const opo = receiveOpo;
    return (
      <>
        <div className="modal show d-block" tabIndex={-1} style={{ zIndex: 1055 }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Receive {opo.opo_number}
                  <span className="small text-muted ms-2">{opo.vendor_name || ''}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setReceiveOpo(null)}></button>
              </div>
              <div className="modal-body">
                <p className="small text-muted mb-2">
                  Quantities are what came back <strong>now</strong> (additive, in the manifest
                  line’s units). A line completes its coating task once returned + scrapped
                  covers what was sent.
                </p>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Part / Job</th>
                        <th className="text-end">Sent</th>
                        <th className="text-end">Already back</th>
                        <th style={{ width: 120 }}>Received now</th>
                        <th style={{ width: 110 }}>Scrapped now</th>
                        <th style={{ width: 110 }}>
                          Close short
                          <div className="small text-muted" style={{ fontWeight: 400 }}>
                            vendor keeps/lost the balance — books it as scrap so the task completes
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(opo.lines || []).map((l) => {
                        const row = receiveRows[l.opo_line_id] || { ret: '0', scr: '0', close: false };
                        const already = lineBack(l);
                        const remaining = lineRemaining(l);
                        return (
                          <tr key={l.opo_line_id}>
                            <td>{l.line_number}</td>
                            <td className="small">
                              {lineIdentity(l)}
                              {remaining === 0 && (
                                <span className="badge bg-success ms-2">complete</span>
                              )}
                            </td>
                            <td className="text-end">{fmtQty(l.quantity_sent)}</td>
                            <td className="text-end small">
                              {fmtQty(already)}
                              {Number(l.quantity_scrapped || 0) > 0 && (
                                <span className="text-danger ms-1">
                                  ({fmtQty(l.quantity_scrapped)} scrap)
                                </span>
                              )}
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                min={0}
                                max={remaining}
                                step="any"
                                value={row.ret}
                                disabled={remaining === 0}
                                onChange={(e) =>
                                  setReceiveRows((prev) => ({
                                    ...prev,
                                    [l.opo_line_id]: { ...row, ret: e.target.value },
                                  }))
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                min={0}
                                max={remaining}
                                step="any"
                                value={row.scr}
                                disabled={remaining === 0}
                                onChange={(e) =>
                                  setReceiveRows((prev) => ({
                                    ...prev,
                                    [l.opo_line_id]: { ...row, scr: e.target.value },
                                  }))
                                }
                              />
                            </td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!row.close}
                                disabled={remaining === 0}
                                onChange={(e) =>
                                  setReceiveRows((prev) => ({
                                    ...prev,
                                    [l.opo_line_id]: { ...row, close: e.target.checked },
                                  }))
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {office && (
                <div className="row g-2 mt-1" data-testid="receive-invoice-fields">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">Vendor invoice # (optional)</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={receiveInvoice}
                      onChange={(e) => setReceiveInvoice(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">
                      Actual cost $ <span className="text-muted fw-normal">(from vendor invoice)</span>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min={0}
                      step="0.01"
                      value={receiveCost}
                      onChange={(e) => setReceiveCost(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">Freight $ (optional)</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      min={0}
                      step="0.01"
                      value={receiveFreight}
                      onChange={(e) => setReceiveFreight(e.target.value)}
                    />
                  </div>
                </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" disabled={busy} onClick={() => setReceiveOpo(null)}>
                  Cancel
                </button>
                <button className="btn btn-success" disabled={busy} onClick={handleReceiveSubmit}>
                  {busy ? 'Receiving…' : 'Receive (PIN)'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop show" style={{ zIndex: 1050 }}></div>
      </>
    );
  }

  // ---- Page ------------------------------------------------------------------

  const stagingCount = board.counts?.staging ?? board.staging.length;
  // ⚠ WHY THIS TAB EXISTS. A defect books a loss but does not remove the downstream tasks
  // already generated, so the queue can offer work for parts that are in a scrap bin. On
  // SO-1119 it offered three brackets when two existed — sending that means paying a coater
  // to powder-coat a scrapped part, on a manifest line that can never be received. The API
  // now withholds those rows from Staging, and this is where they surface. They are NOT
  // selectable: there is nothing to send.
  function renderShortSupplyTab() {
    const rows = board.short_supply || [];
    if (!rows.length) {
      return <div className="alert alert-secondary">Nothing is short — every staged task has parts behind it.</div>;
    }
    return (
      <>
        <div className="alert alert-warning">
          <strong>These tasks have no parts behind them.</strong> They are still on the job, but
          the units they were created for were defected and not replaced, so there is nothing to
          send to a vendor. Fix the count on the job — or raise a remake — rather than staging
          them.
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                {/* No vendor column: the API decorates vendor/assembly onto STAGED rows only,
                    so it would be permanently blank here — and a shortage is fixed on the job,
                    not by knowing which coater it was destined for. */}
                <th>Part</th><th>Job #</th><th>Qty</th><th>Why it is not staged</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.task_id}>
                  <td>{renderPartCell(t.product_id, t.part_description, t.image_path, t.assembly_name)}</td>
                  <td className="text-nowrap">#{t.master_job_num}</td>
                  <td className="text-nowrap">{t.quantity}</td>
                  <td className="small text-muted">{t.short_reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  const tabs = operator ? [
    // the floor's words (Shipping hub): the paper is made on To send, loaded on Loading, received back at vendor
    { id: 'staging', label: 'To send', count: stagingCount },
    { id: 'drafts', label: 'Loading', count: draftManifests.length },
    { id: 'vendor', label: 'At vendor (Receive back)', count: atVendorOpos.length },
    { id: 'history', label: 'History', count: historyOpos.length },
  ] : [
    { id: 'staging', label: 'Staging', count: stagingCount },
    { id: 'drafts', label: 'Manifests', count: draftManifests.length },
    { id: 'vendor', label: 'At Vendor', count: atVendorOpos.length },
    { id: 'history', label: 'History', count: historyOpos.length },
    // Only ever shown when there IS something short. A permanent empty tab teaches the
    // office to stop looking at it, and this is the one that costs money when ignored.
    ...((board.short_supply || []).length
      ? [{ id: 'short', label: 'Short', count: board.short_supply.length }]
      : []),
  ];

  return (
    <div className={operator ? '' : 'container my-4'} data-testid={operator ? 'outsourcing-operator' : undefined}>
      {operator ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {officeUnlocked ? (
            <button className="btn btn-warning" style={{ minHeight: 44 }} onClick={() => setOfficeUnlocked(false)}>
              Office tools on · Lock
            </button>
          ) : (
            <button className="btn btn-outline-secondary" style={{ minHeight: 44 }} onClick={unlockOffice}>
              Office tools
            </button>
          )}
          <button className="btn btn-outline-secondary ms-auto" style={{ minHeight: 44 }} onClick={loadAll} disabled={loading}>
            ↻ Refresh
          </button>
        </div>
      ) : (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h1 className="text-primary mb-0">Outsourcing</h1>
          <button className="btn btn-sm btn-outline-secondary" onClick={loadAll} disabled={loading}>
            ↻ Refresh
          </button>
        </div>
      )}

      <ul className="nav nav-tabs mb-4">
        {tabs.map((t) => (
          <li className="nav-item" key={t.id}>
            <button
              className={`nav-link ${currentTab === t.id ? 'active' : ''}`}
              style={operator ? { minHeight: 44 } : undefined}
              onClick={() => setCurrentTab(t.id)}
            >
              {t.label}
              {t.count > 0 && (
                <span className={`badge ms-2 ${currentTab === t.id ? 'bg-primary' : 'bg-secondary'}`}>
                  {t.count}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {loadError && (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <span>{loadError}</span>
          <button className="btn btn-sm btn-outline-danger" onClick={loadAll}>
            Retry
          </button>
        </div>
      )}
      {loading && (
        <div className="text-center p-4">
          <div className="spinner-border text-primary"></div>
        </div>
      )}

      {!loading && currentTab === 'staging' && renderStagingTab()}
      {!loading && currentTab === 'drafts' && renderDraftsTab()}
      {!loading && currentTab === 'vendor' && renderAtVendorTab()}
      {!loading && currentTab === 'history' && renderHistoryTab()}
      {!loading && !operator && currentTab === 'short' && renderShortSupplyTab()}
      {renderInvoiceModal()}

      {renderCreateModal()}
      {renderReceiveModal()}
    </div>
  );
}
